import React, { useEffect, useState } from 'react';
import {
  Box, Typography, Button, TextField, Alert, CircularProgress, Card, Stack, Divider,
  Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Paper,
} from '@mui/material';
import { useTheme } from '../../contexts/ThemeContext';

const API_URL = import.meta.env.VITE_SERVICE_URL_HOSTLY_API;
const API_KEY = import.meta.env.VITE_API_SECRET_KEY;

interface ReportRow {
  affiliateName: string;
  conversions: number;
  commissionPerSale: number;
  commission: number;
}

interface Mapping {
  offerIdentifier: string;
  affiliateName: string;
  commissionPerSale: number;
}

export const AdminAffiliates: React.FC = () => {
  const { theme } = useTheme();
  const [rows, setRows] = useState<ReportRow[]>([]);
  const [total, setTotal] = useState(0);
  const [mappings, setMappings] = useState<Mapping[]>([]);
  const [loading, setLoading] = useState(true);
  const [message, setMessage] = useState<{ type: 'success' | 'error'; text: string } | null>(null);
  const [form, setForm] = useState({ offerIdentifier: '', affiliateName: '', commissionPerSale: '' });
  const [saving, setSaving] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const [rep, maps] = await Promise.all([
        fetch(`${API_URL}/admin/appstore/affiliate-report`, { headers: { 'X-API-Key': API_KEY } }).then(r => r.json()),
        fetch(`${API_URL}/admin/appstore/affiliate-offers`, { headers: { 'X-API-Key': API_KEY } }).then(r => r.json()),
      ]);
      setRows(rep.rows || []);
      setTotal(rep.totalCommission || 0);
      setMappings(maps.items || []);
    } catch (e: any) {
      setMessage({ type: 'error', text: e.message || 'Erro ao carregar' });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const save = async () => {
    if (!form.offerIdentifier.trim() || !form.affiliateName.trim()) {
      setMessage({ type: 'error', text: 'Código e afiliada são obrigatórios' });
      return;
    }
    setSaving(true);
    setMessage(null);
    try {
      const res = await fetch(`${API_URL}/admin/appstore/affiliate-offers`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'X-API-Key': API_KEY },
        body: JSON.stringify({
          offerIdentifier: form.offerIdentifier.trim(),
          affiliateName: form.affiliateName.trim(),
          commissionPerSale: parseFloat(form.commissionPerSale.replace(',', '.')) || 0,
        }),
      });
      if (!res.ok) throw new Error((await res.json()).error || 'Erro ao salvar');
      setMessage({ type: 'success', text: 'Vínculo salvo' });
      setForm({ offerIdentifier: '', affiliateName: '', commissionPerSale: '' });
      load();
    } catch (e: any) {
      setMessage({ type: 'error', text: e.message });
    } finally {
      setSaving(false);
    }
  };

  const brl = (v: number) => v.toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' });

  return (
    <Box>
      <Typography variant="h5" sx={{ color: theme.colors.text.primary, mb: 1 }}>Afiliadas · comissão</Typography>
      <Typography variant="body2" sx={{ color: theme.colors.text.secondary, mb: 2 }}>
        Vincule o offer code da Apple (ou promo Stripe) de cada organizer. A venda é atribuída automaticamente pelo código usado; a comissão sai do nº de vendas × valor por venda.
      </Typography>

      {message && <Alert severity={message.type} sx={{ mb: 2 }} onClose={() => setMessage(null)}>{message.text}</Alert>}

      <Card sx={{ p: 2, mb: 3, backgroundColor: theme.colors.card, border: `1px solid ${theme.colors.border}` }}>
        <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 2, color: theme.colors.text.primary }}>Vincular código a uma afiliada</Typography>
        <Stack direction={{ xs: 'column', md: 'row' }} spacing={2}>
          <TextField label="Código (offerIdentifier)" value={form.offerIdentifier} onChange={e => setForm({ ...form, offerIdentifier: e.target.value })} fullWidth size="small" />
          <TextField label="Afiliada" value={form.affiliateName} onChange={e => setForm({ ...form, affiliateName: e.target.value })} fullWidth size="small" />
          <TextField label="Comissão por venda (R$)" value={form.commissionPerSale} onChange={e => setForm({ ...form, commissionPerSale: e.target.value })} size="small" sx={{ minWidth: 180 }} inputProps={{ inputMode: 'decimal' }} />
          <Button variant="contained" onClick={save} disabled={saving} sx={{ minWidth: 120, backgroundColor: theme.colors.primary }}>
            {saving ? <CircularProgress aria-label="Carregando" size={20} /> : 'Salvar'}
          </Button>
        </Stack>
        {mappings.length > 0 && (
          <Typography variant="caption" sx={{ color: theme.colors.text.secondary, display: 'block', mt: 2 }}>
            {mappings.map(m => `${m.affiliateName} → ${m.offerIdentifier}`).join(' · ')}
          </Typography>
        )}
      </Card>

      <Divider sx={{ my: 2 }} />

      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}><CircularProgress aria-label="Carregando" size={28} /></Box>
      ) : (
        <TableContainer component={Paper} sx={{ backgroundColor: theme.colors.card }}>
          <Table sx={{ '& td, & th': { fontVariantNumeric: 'tabular-nums' } }}>
            <TableHead>
              <TableRow>
                <TableCell sx={{ fontWeight: 'bold' }}>Afiliada</TableCell>
                <TableCell sx={{ fontWeight: 'bold' }} align="right">Vendas</TableCell>
                <TableCell sx={{ fontWeight: 'bold' }} align="right">Por venda</TableCell>
                <TableCell sx={{ fontWeight: 'bold' }} align="right">Comissão</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {rows.length === 0 ? (
                <TableRow><TableCell colSpan={4} align="center" sx={{ py: 4, color: theme.colors.text.secondary }}>Nenhuma venda atribuída ainda.</TableCell></TableRow>
              ) : (
                <>
                  {rows.map(r => (
                    <TableRow key={r.affiliateName}>
                      <TableCell sx={{ fontWeight: 600 }}>{r.affiliateName}</TableCell>
                      <TableCell align="right">{r.conversions}</TableCell>
                      <TableCell align="right">{brl(r.commissionPerSale)}</TableCell>
                      <TableCell align="right" sx={{ fontWeight: 700 }}>{brl(r.commission)}</TableCell>
                    </TableRow>
                  ))}
                  <TableRow>
                    <TableCell sx={{ fontWeight: 700 }}>Total</TableCell>
                    <TableCell />
                    <TableCell />
                    <TableCell align="right" sx={{ fontWeight: 700 }}>{brl(total)}</TableCell>
                  </TableRow>
                </>
              )}
            </TableBody>
          </Table>
        </TableContainer>
      )}
    </Box>
  );
};
